import type { components } from "../../../api/generated";
import type {
  OpportunityContactOption,
  OpportunityCreatePayload,
  OpportunityOwnerReassignPayload,
  OpportunityOwnerReassignResult,
  OpportunityCustomerOption,
  OpportunityCustomizationProjectItem,
  OpportunityCustomizationProjectList,
  OpportunityCustomizationCandidate,
  OpportunityCustomizationCandidateList,
  OpportunityCustomizationReassignResult,
  OpportunityFollowupCreatePayload,
  OpportunityFollowupListPage,
  OpportunityFollowupRecord,
  OpportunityOrganizationOption,
  OpportunitySelectOption,
  OpportunityStaffOption,
  OpportunityStageLogItem,
  OpportunityStageLogList,
  OpportunityStageBoard,
  OpportunityStageNode,
  OpportunityStageOwnerBulkAssignPayload,
  OpportunityStageOwnerItem,
  OpportunityStageOwnerList,
  OpportunitySplitPayload,
  OpportunitySplitPreview,
  OpportunitySplitResult,
  OpportunityWorkbenchItem,
  OpportunityWorkbenchListParams,
  OpportunityWorkbenchListPage,
  OpportunityWorkbenchMetadata,
  OpportunityQuickCreateCheck,
  OpportunityQuickCreateCandidate,
  OpportunityQuickCreateContext,
  OpportunityQuickCreateExistingItem,
} from "../types";

export type OpportunityWorkbenchItemRaw = components["schemas"]["OpportunityItem"] & {
  stage_source?: string;
  stage_evaluated_at?: string | null;
  stage_stalled_since?: string | null;
  stage_stalled_days?: number;
  stage_stalled?: boolean;
  customization_project_count?: number;
  selected_customization_has_itinerary?: boolean | null;
  selected_customization_is_formed?: boolean;
  has_transferable_customization?: boolean;
  transferable_customization_count?: number;
  sole_transferable_project_id?: string | null;
  sole_transferable_project_name?: string | null;
  sole_transferable_is_formed?: boolean;
  access_policy?: components["schemas"]["OpportunityAccessPolicy"] | null;
};
export type OpportunityQuickCreateCheckRaw = components["schemas"]["OpportunityQuickCreateCheck"];
type OpportunityStageLogItemRawExtended = components["schemas"]["OpportunityStageLogItem"] & {
  snapshot_json?: Record<string, unknown> | null;
};
export type OpportunityStageBoardRaw = {
  opportunity_id: number;
  opportunity_code: string;
  opportunity_name: string;
  current_stage?: string | null;
  current_stage_label?: string | null;
  stage_source?: string;
  stage_stalled_since?: string | null;
  stage_stalled_days?: number;
  stage_stalled?: boolean;
  stage_history_missing?: boolean;
  manual_stage_code?: string | null;
  manual_stage_note?: string | null;
  manual_stage_set_by_user_id?: number | null;
  manual_stage_set_at?: string | null;
  nodes: Array<{
    code: string; label: string; sequence: number; kind: "manual" | "derived";
    status: "not_started" | "current" | "completed" | "manual_set";
    last_changed_at?: string | null; changed_by_user_name?: string | null;
    trigger_source?: string | null; summary?: string | null; evidence_count?: number;
    manual_note?: string | null; history?: OpportunityStageLogItemRawExtended[];
  }>;
};
export type OpportunityWorkbenchListPageRaw = components["schemas"]["OpportunityListPage"];
export type OpportunitySelectOptionRaw = components["schemas"]["OpportunitySelectOption"];
export type OpportunityStaffOptionRaw = components["schemas"]["OpportunityStaffOption"];
export type OpportunityWorkbenchMetadataRaw = components["schemas"]["OpportunityMetadata"];
export type OpportunityOrganizationOptionRaw =
  components["schemas"]["OpportunityOrganizationOption"];
export type OpportunityCustomerOptionRaw = components["schemas"]["OpportunityCustomerOption"];
export type OpportunityContactOptionRaw = components["schemas"]["OpportunityContactOption"];
export type OpportunityFollowupRecordRaw = components["schemas"]["OpportunityFollowupRecord"];
export type OpportunityFollowupListPageRaw = components["schemas"]["OpportunityFollowupListPage"];
export type OpportunityStageLogItemRaw = OpportunityStageLogItemRawExtended;
export type OpportunityStageLogListRaw = components["schemas"]["OpportunityStageLogList"];
export type OpportunityStageOwnerItemRaw = components["schemas"]["OpportunityStageOwnerItem"];
export type OpportunityStageOwnerListRaw = components["schemas"]["OpportunityStageOwnerList"];
export type OpportunityCustomizationProjectItemRaw =
  components["schemas"]["OpportunityCustomizationProjectItem"];
export type OpportunityCustomizationProjectListRaw =
  components["schemas"]["OpportunityCustomizationProjectList"];
export type OpportunityCustomizationCandidateItemRaw =
  components["schemas"]["OpportunityCustomizationCandidateItem"];
export type OpportunityCustomizationCandidateListRaw =
  components["schemas"]["OpportunityCustomizationCandidateList"];
export type OpportunityCustomizationReassignResultRaw =
  components["schemas"]["OpportunityCustomizationReassignResult"];
export type OpportunitySplitPreviewRaw = components["schemas"]["OpportunitySplitPreviewData"];
export type OpportunitySplitResultRaw = components["schemas"]["OpportunitySplitResult"];
export type OpportunityOwnerReassignResultRaw = components["schemas"]["OpportunityOwnerReassignResult"];

/** 映射商机新增请求。 */
export function mapOpportunityCreatePayload(payload: OpportunityCreatePayload) {
  return {
    opportunity_name: payload.opportunityName.trim(),
    org_id: payload.orgId,
    college_unit_id: payload.collegeUnitId,
    remark: payload.remark.trim(),
    travel_month: normalizeOptionalText(payload.travelMonth),
    customer_id: payload.customerId,
    contact_id: payload.contactId,
    traveler_count: payload.travelerCount,
    study_destination: normalizeOptionalText(payload.studyDestination),
    study_day_count: payload.studyDayCount,
    owner_user_id: payload.ownerUserId,
  };
}

/** 映射批量转派商机请求。 */
export function mapOpportunityOwnerReassignPayload(payload: OpportunityOwnerReassignPayload) {
  return {
    opportunity_ids: payload.opportunityIds,
    owner_user_id: payload.ownerUserId,
  };
}

/** 映射批量转派商机结果。 */
export function mapOpportunityOwnerReassignResult(
  raw: OpportunityOwnerReassignResultRaw,
): OpportunityOwnerReassignResult {
  return {
    updated: raw.updated,
    updatedCount: raw.updated_count,
    opportunityIds: raw.opportunity_ids ?? [],
    ownerUserId: raw.owner_user_id,
    operationAt: raw.operation_at,
  };
}

/** 映射商机列表查询参数。 */
export function mapOpportunityListQueryParams(params: OpportunityWorkbenchListParams) {
  return {
    page: params.page,
    page_size: params.pageSize,
    keyword: normalizeOptionalText(params.keyword),
    org_id: params.orgId,
    customer_keyword: normalizeOptionalText(params.customerKeyword),
    customization_keyword: normalizeOptionalText(params.customizationKeyword),
    travel_month: normalizeOptionalText(params.travelMonth),
    stage: normalizeOptionalText(params.stage),
    stages: params.stages,
    status: normalizeOptionalText(params.status),
    sign_info_statuses: params.signInfoStatuses,
    owner_user_id: params.ownerUserId,
    has_selected_customization: params.hasSelectedCustomization,
    sort_field: params.sortField,
    sort_direction: params.sortDirection,
  };
}

/** 映射商机列表项。 */
export function mapOpportunityWorkbenchItem(raw: OpportunityWorkbenchItemRaw): OpportunityWorkbenchItem {
  return {
    id: raw.id,
    opportunityCode: raw.opportunity_code,
    opportunityName: raw.opportunity_name,
    orgId: raw.org_id,
    orgName: raw.org_name ?? undefined,
    collegeUnitId: raw.college_unit_id ?? undefined,
    collegeUnitName: raw.college_unit_name ?? undefined,
    travelMonth: raw.travel_month ?? undefined,
    customerId: raw.customer_id ?? undefined,
    customerName: raw.customer_name ?? undefined,
    contactId: raw.contact_id ?? undefined,
    contactName: raw.contact_name ?? undefined,
    travelerCount: raw.traveler_count ?? undefined,
    studyDestination: raw.study_destination ?? undefined,
    studyDayCount: raw.study_day_count ?? undefined,
    stage: raw.stage,
    stageSource: (raw.stage_source as OpportunityWorkbenchItem["stageSource"]) ?? "unknown",
    stageEvaluatedAt: raw.stage_evaluated_at ?? undefined,
    stageStalledSince: raw.stage_stalled_since ?? undefined,
    stageStalledDays: raw.stage_stalled_days ?? 0,
    stageStalled: raw.stage_stalled ?? false,
    status: raw.status,
    ownerUserId: raw.owner_user_id ?? undefined,
    ownerUserName: raw.owner_user_name ?? undefined,
    createdByUserId: raw.created_by_user_id ?? undefined,
    createdByUserName: raw.created_by_user_name ?? undefined,
    selectedCustomizationProjectId: raw.selected_customization_project_id ?? undefined,
    selectedCustomizationProjectName: raw.selected_customization_project_name ?? undefined,
    selectedCustomizationCount: raw.selected_customization_count ?? 0,
    selectedCustomizationHasItinerary:
      raw.selected_customization_has_itinerary === undefined
        ? undefined
        : raw.selected_customization_has_itinerary,
    selectedCustomizationIsFormed: raw.selected_customization_is_formed ?? false,
    hasTransferableCustomization: raw.has_transferable_customization ?? false,
    transferableCustomizationCount: raw.transferable_customization_count ?? 0,
    soleTransferableProjectId: raw.sole_transferable_project_id ?? undefined,
    soleTransferableProjectName: raw.sole_transferable_project_name ?? undefined,
    soleTransferableIsFormed: raw.sole_transferable_is_formed ?? false,
    candidateCustomizationCount: raw.candidate_customization_count ?? 0,
    customizationProjectCount: raw.customization_project_count ?? 0,
    accessPolicy: raw.access_policy
      ? {
          canEdit: raw.access_policy.can_edit,
          canCreateCustomization: raw.access_policy.can_create_customization,
          canManageCustomizationAssignment:
            (raw.access_policy as typeof raw.access_policy & {
              can_manage_customization_assignment?: boolean;
            }).can_manage_customization_assignment,
          canSplitOpportunity: raw.access_policy.can_split_opportunity,
          canAssignOwner: raw.access_policy.can_assign_owner,
          canAssignStageOwner: raw.access_policy.can_assign_stage_owner,
          canOperateStage: raw.access_policy.can_operate_stage,
          canManageManualStage:
            (raw.access_policy as typeof raw.access_policy & {
              can_manage_manual_stage?: boolean;
            }).can_manage_manual_stage,
        }
      : undefined,
    latestFollowupContent: raw.latest_followup_content ?? undefined,
    latestFollowupAt: raw.latest_followup_at ?? undefined,
    latestItineraryId: raw.latest_itinerary_id ?? undefined,
    latestItineraryUpdatedAt: raw.latest_itinerary_updated_at ?? undefined,
    latestItineraryProjectCode: raw.latest_itinerary_project_code ?? undefined,
    latestItineraryDemandVersionNumber:
      raw.latest_itinerary_demand_version_number ?? undefined,
    latestItineraryProjectName: raw.latest_itinerary_project_name ?? undefined,
    latestItineraryTitle: raw.latest_itinerary_title ?? undefined,
    latestItineraryDestinationSummary:
      raw.latest_itinerary_destination_summary ?? undefined,
    signInfo: mapOpportunitySignInfoSummary(
      (raw as OpportunityWorkbenchItemRaw & {
        sign_info?: {
          status?: string;
          is_sign?: boolean | null;
          opportunity_progress?: string | null;
          reason_type?: string | null;
          busi_source_type?: string | null;
          team_summary?: string | null;
          reason_remark?: string | null;
          competitor_reason?: string | null;
          decision_maker_text?: string | null;
        } | null;
      }).sign_info,
    ),
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    remark: raw.remark ?? undefined,
  };
}

/** 映射列表项内嵌的商机进度摘要。 */
function mapOpportunitySignInfoSummary(
  raw:
    | {
        status?: string;
        is_sign?: boolean | null;
        opportunity_progress?: string | null;
        reason_type?: string | null;
        busi_source_type?: string | null;
        team_summary?: string | null;
        reason_remark?: string | null;
        competitor_reason?: string | null;
        decision_maker_text?: string | null;
      }
    | null
    | undefined,
): OpportunityWorkbenchItem["signInfo"] {
  if (!raw) {
    return { status: "unfilled" };
  }
  const status =
    raw.status === "won" || raw.status === "lost" || raw.status === "unfilled"
      ? raw.status
      : "unfilled";
  const opportunityProgress =
    raw.opportunity_progress === "50%" ||
    raw.opportunity_progress === "80%" ||
    raw.opportunity_progress === "100%"
      ? raw.opportunity_progress
      : undefined;
  return {
    status,
    isSign: raw.is_sign ?? undefined,
    opportunityProgress,
    reasonType: raw.reason_type ?? undefined,
    busiSourceType: raw.busi_source_type ?? undefined,
    teamSummary: raw.team_summary ?? undefined,
    reasonRemark: raw.reason_remark ?? undefined,
    competitorReason: raw.competitor_reason ?? undefined,
    decisionMakerText: raw.decision_maker_text ?? undefined,
  };
}

/** 映射联系人动态快捷建商机校验结果。 */
export function mapOpportunityQuickCreateCheck(
  raw: OpportunityQuickCreateCheckRaw,
): OpportunityQuickCreateCheck {
  const context = raw.context;
  return {
    context: context
      ? mapOpportunityQuickCreateContext(context)
      : undefined,
    candidates: (raw.candidates ?? []).map(mapOpportunityQuickCreateCandidate),
    blocked: raw.blocked,
    blockedReason: raw.blocked_reason ?? undefined,
  };
}

/** 映射快捷建商机归属上下文。 */
function mapOpportunityQuickCreateContext(
  raw: components["schemas"]["OpportunityQuickCreateContext"],
): OpportunityQuickCreateContext {
  return {
    contactId: raw.contact_id,
    contactName: raw.contact_name,
    orgId: raw.org_id,
    orgName: raw.org_name,
    mainOrgUnitId: raw.main_org_unit_id ?? undefined,
    collegeUnitId: raw.college_unit_id ?? undefined,
    collegeUnitName: raw.college_unit_name ?? undefined,
    customerId: raw.customer_id ?? undefined,
    customerName: raw.customer_name ?? undefined,
    labelsSnapshot: raw.labels_snapshot ?? undefined,
  };
}

/** 映射快捷建商机单个月份候选。 */
function mapOpportunityQuickCreateCandidate(
  raw: components["schemas"]["OpportunityQuickCreateCandidate"],
): OpportunityQuickCreateCandidate {
  return {
    travelMonth: raw.travel_month,
    canCreate: raw.can_create,
    reasonCode: raw.reason_code ?? undefined,
    reason: raw.reason ?? undefined,
    existingOpportunities: (raw.existing_opportunities ?? []).map(
      (item): OpportunityQuickCreateExistingItem => ({
        id: item.id,
        opportunityCode: item.opportunity_code,
        opportunityName: item.opportunity_name,
        travelMonth: item.travel_month ?? undefined,
      }),
    ),
  };
}

/** 映射商机拆分预览。 */
export function mapOpportunitySplitPreview(
  raw: OpportunitySplitPreviewRaw,
): OpportunitySplitPreview {
  return {
    opportunityId: raw.opportunity_id,
    opportunityCode: raw.opportunity_code,
    sourceOpportunity: {
      opportunityId: raw.source_opportunity.opportunity_id,
      opportunityCode: raw.source_opportunity.opportunity_code,
      opportunityName: raw.source_opportunity.opportunity_name,
      orgId: raw.source_opportunity.org_id,
      orgName: raw.source_opportunity.org_name,
      ownerUserId: raw.source_opportunity.owner_user_id ?? undefined,
      ownerUserName: raw.source_opportunity.owner_user_name ?? undefined,
      projectCount: raw.source_opportunity.project_count,
      orderCount: raw.source_opportunity.order_count,
    },
    previewToken: raw.preview_token,
    recommendedOriginalGroupKey: raw.recommended_original_group_key,
    blockingConflicts: raw.blocking_conflicts ?? [],
    projects: (raw.projects ?? []).map((project) => ({
      projectId: project.project_id,
      projectCode: project.project_code,
      projectName: project.project_name,
      orgId: project.org_id ?? undefined,
      customerId: project.customer_id ?? undefined,
      contactId: project.contact_id ?? undefined,
      majorNames: project.major_names ?? [],
      peopleCount: project.people_count ?? undefined,
      travelMonth: project.travel_month ?? undefined,
      durationDays: project.duration_days ?? undefined,
      destinationProvinces: project.destination_provinces ?? [],
      stayAreaNames: project.stay_area_names ?? [],
      demandId: project.demand_id,
      demandVersionSeq: project.demand_version_seq,
      projectUpdatedAt: project.project_updated_at,
      demandUpdatedAt: project.demand_updated_at,
      selected: project.selected,
      hasEffectiveOrder: project.has_effective_order,
      orders: (project.orders ?? []).map((order) => ({
        orderId: order.order_id,
        status: order.status,
        teamId: order.team_id ?? undefined,
        travelerCount: order.traveler_count ?? undefined,
      })),
    })),
    groups: (raw.groups ?? []).map((group) => ({
      groupKey: group.group_key,
      projectIds: group.project_ids ?? [],
      recommendation: group.recommendation,
      derivedFields: {
        orgId: group.derived_fields.org_id ?? undefined,
        customerId: group.derived_fields.customer_id ?? undefined,
        contactId: group.derived_fields.contact_id ?? undefined,
        travelMonth: group.derived_fields.travel_month ?? undefined,
        travelerCount: group.derived_fields.traveler_count ?? undefined,
        studyDestination: group.derived_fields.study_destination ?? undefined,
        studyDayCount: group.derived_fields.study_day_count ?? undefined,
      },
      hint: group.hint ?? undefined,
    })),
    comparisons: (raw.comparisons ?? []).map((comparison) => ({
      leftProjectId: comparison.left_project_id,
      rightProjectId: comparison.right_project_id,
      decision: comparison.decision,
      confidence: comparison.confidence,
      recommendedGroupKey: comparison.recommended_group_key ?? undefined,
      matchedFields: comparison.matched_fields ?? [],
      differentFields: comparison.different_fields ?? [],
      unknownFields: comparison.unknown_fields ?? [],
      fulfillmentSplitHint: comparison.fulfillment_split_hint,
      explanation: comparison.explanation,
    })),
  };
}

/** 映射商机拆分请求。 */
export function mapOpportunitySplitPayload(payload: OpportunitySplitPayload) {
  return {
    preview_token: payload.previewToken,
    original_group_key: payload.originalGroupKey,
    groups: payload.groups.map((group) => ({
      group_key: group.groupKey,
      project_ids: group.projectIds,
    })),
  };
}

/** 映射商机拆分提交结果。 */
export function mapOpportunitySplitResult(raw: OpportunitySplitResultRaw): OpportunitySplitResult {
  return {
    operationId: raw.operation_id,
    sourceOpportunityId: raw.source_opportunity_id,
    mappings: (raw.mappings ?? []).map((item) => ({
      projectId: item.project_id,
      opportunityId: item.opportunity_id,
      opportunityCode: item.opportunity_code,
    })),
    createdOpportunityIds: raw.created_opportunity_ids ?? [],
    legacySyncStatus: raw.legacy_sync_status,
    legacySyncErrorSummary: raw.legacy_sync_error_summary ?? undefined,
    idempotentReplay: raw.idempotent_replay,
  };
}

/** 映射商机列表分页。 */
export function mapOpportunityListPage(raw: OpportunityWorkbenchListPageRaw): OpportunityWorkbenchListPage {
  return {
    items: (raw.items ?? []).map(mapOpportunityWorkbenchItem),
    total: raw.total,
    page: raw.page,
    pageSize: raw.page_size,
  };
}

/** 映射商机元数据。 */
export function mapOpportunityMetadata(raw: OpportunityWorkbenchMetadataRaw): OpportunityWorkbenchMetadata {
  return {
    staffOptions: (raw.staff_options ?? []).map(mapStaffOption),
    stageOptions: (raw.stage_options ?? []).map(mapSelectOption),
    statusOptions: (raw.status_options ?? []).map(mapSelectOption),
    stageGroupOptions: (raw.stage_group_options ?? []).map(mapSelectOption),
    followupTypeOptions: (raw.followup_type_options ?? []).map(mapSelectOption),
    signBusiSourceOptions: (raw.sign_busi_source_options ?? []).map(mapSelectOption),
    signWinReasonOptions: (raw.sign_win_reason_options ?? []).map(mapSelectOption),
    signLoseReasonOptions: (raw.sign_lose_reason_options ?? []).map(mapSelectOption),
  };
}

/** 映射学校候选项。 */
export function mapOpportunityOrganizationOption(
  raw: OpportunityOrganizationOptionRaw,
): OpportunityOrganizationOption {
  return {
    id: raw.id,
    orgName: raw.org_name,
    province: raw.province ?? undefined,
    city: raw.city ?? undefined,
  };
}

/** 映射客户候选项。 */
export function mapOpportunityCustomerOption(
  raw: OpportunityCustomerOptionRaw,
): OpportunityCustomerOption {
  return {
    id: raw.id,
    customerName: raw.customer_name,
    orgId: raw.org_id,
  };
}

/** 映射联系人候选项。 */
export function mapOpportunityContactOption(
  raw: OpportunityContactOptionRaw,
): OpportunityContactOption {
  return {
    id: raw.id,
    contactName: raw.contact_name,
    orgId: raw.org_id,
    customerId: raw.customer_id ?? undefined,
    positionName: raw.position_name ?? undefined,
    mobile: raw.mobile ?? undefined,
  };
}

/** 映射跟进记录新增请求。 */
export function mapFollowupCreatePayload(payload: OpportunityFollowupCreatePayload) {
  return {
    followup_type: payload.followupType,
    content: payload.content,
    current_user_id: payload.currentUserId,
  };
}

/** 映射输赢单保存请求。 */
export function mapSignInfoSavePayload(payload: {
  opportunityProgress: "50%" | "80%";
  isSign?: boolean;
  reasonType?: string;
  busiSourceType?: string;
  teamSummary?: string;
  reasonRemark?: string;
  competitorReason?: string;
  decisionMakerText?: string;
}) {
  return {
    opportunity_progress: payload.opportunityProgress,
    is_sign: payload.isSign,
    reason_type: payload.reasonType,
    busi_source_type: payload.busiSourceType,
    team_summary: payload.teamSummary,
    reason_remark: payload.reasonRemark,
    competitor_reason: payload.competitorReason,
    decision_maker_text: payload.decisionMakerText,
  };
}

/** 映射输赢单详情。 */
export function mapSignInfoDetail(raw: {
  opportunity_id: number;
  status: string;
  is_sign?: boolean | null;
  opportunity_progress?: string | null;
  reason_type?: string | null;
  busi_source_type?: string | null;
  team_summary?: string | null;
  reason_remark?: string | null;
  competitor_reason?: string | null;
  decision_maker_text?: string | null;
  created_by_user_id?: number | null;
  updated_by_user_id?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
}): import("../types").OpportunitySignInfoDetail {
  const summary = mapOpportunitySignInfoSummary(raw);
  return {
    opportunityId: raw.opportunity_id,
    status: summary?.status ?? "unfilled",
    isSign: summary?.isSign,
    opportunityProgress: summary?.opportunityProgress ?? "50%",
    reasonType: summary?.reasonType,
    busiSourceType: summary?.busiSourceType,
    teamSummary: summary?.teamSummary,
    reasonRemark: summary?.reasonRemark,
    competitorReason: summary?.competitorReason,
    decisionMakerText: summary?.decisionMakerText,
    createdByUserId: raw.created_by_user_id ?? undefined,
    updatedByUserId: raw.updated_by_user_id ?? undefined,
    createdAt: raw.created_at ?? undefined,
    updatedAt: raw.updated_at ?? undefined,
  };
}

/** 映射跟进记录。 */
export function mapFollowupRecord(raw: OpportunityFollowupRecordRaw): OpportunityFollowupRecord {
  return {
    id: raw.id,
    opportunityId: raw.opportunity_id,
    followupType: raw.followup_type,
    content: raw.content,
    occurredAt: raw.occurred_at,
    recordedByUserId: raw.recorded_by_user_id ?? undefined,
    recordedByUserName: raw.recorded_by_user_name ?? undefined,
    isDeleted: raw.is_deleted ?? false,
    sourceType: raw.source_type ?? "manual",
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  };
}

/** 映射跟进记录分页。 */
export function mapFollowupListPage(raw: OpportunityFollowupListPageRaw): OpportunityFollowupListPage {
  return {
    items: (raw.items ?? []).map(mapFollowupRecord),
    total: raw.total,
    page: raw.page,
    pageSize: raw.page_size,
  };
}

/** 映射阶段日志列表。 */
export function mapStageLogList(raw: OpportunityStageLogListRaw): OpportunityStageLogList {
  return {
    items: (raw.items ?? []).map(mapStageLogItem),
  };
}

/** 映射阶段负责人列表。 */
export function mapStageOwnerList(raw: OpportunityStageOwnerListRaw): OpportunityStageOwnerList {
  return {
    items: (raw.items ?? []).map(mapStageOwnerItem),
  };
}

/** 映射阶段负责人保存请求。 */
export function mapStageOwnerBulkAssignPayload(payload: OpportunityStageOwnerBulkAssignPayload) {
  return {
    assignments: Object.fromEntries(
      Object.entries(payload.assignments).map(([key, value]) => [key, value ?? null]),
    ),
    current_user_id: payload.currentUserId,
    remark: normalizeOptionalText(payload.remark),
  };
}

/** 映射商机下定制项目列表。 */
export function mapCustomizationProjectList(
  raw: OpportunityCustomizationProjectListRaw,
): OpportunityCustomizationProjectList {
  return {
    items: (raw.items ?? []).map(mapCustomizationProjectItem),
  };
}

/** 映射找定制候选列表。 */
export function mapCustomizationCandidateList(
  raw: OpportunityCustomizationCandidateListRaw,
): OpportunityCustomizationCandidateList {
  return {
    total: raw.total ?? 0,
    items: (raw.items ?? []).map(
      (item): OpportunityCustomizationCandidate => ({
        projectId: item.project_id,
        projectCode: item.project_code ?? undefined,
        projectName: item.project_name,
        orgName: item.org_name ?? undefined,
        customerName: item.customer_name ?? undefined,
        contactName: item.contact_name ?? undefined,
        salesUserName: item.sales_user_name ?? undefined,
        dispatcherUserName: item.dispatcher_user_name ?? undefined,
        opportunityId: item.opportunity_id ?? undefined,
        opportunityCode: item.opportunity_code ?? undefined,
        opportunityName: item.opportunity_name ?? undefined,
        currentOpportunity: item.current_opportunity,
        projectUpdatedAt: item.project_updated_at,
        demandId: item.demand_id ?? undefined,
        demandVersionNumber: item.demand_version_number ?? undefined,
        majorNames: item.major_names ?? [],
        peopleCount: item.people_count ?? undefined,
        durationDays: item.duration_days ?? undefined,
        travelMonth: item.travel_month ?? undefined,
        departureStartDate: item.departure_start_date ?? undefined,
        destinationProvinces: item.destination_provinces ?? [],
        stayAreaNames: item.stay_area_names ?? [],
        hasItinerary: item.has_itinerary,
        hasBudget: item.has_budget,
        hasOrder: item.has_order,
        matchScore: item.match_score,
        matchLevel: item.match_level,
        matchedDimensions: item.matched_dimensions ?? [],
        differentDimensions: item.different_dimensions ?? [],
        unknownDimensions: item.unknown_dimensions ?? [],
        selectable: item.selectable,
        unselectableReason: item.unselectable_reason ?? undefined,
        legacyWritebackReadiness:
          item.legacy_writeback_readiness === "ready" ? "ready" : "identity_pending",
      }),
    ),
  };
}

/** 映射找定制提交时实际执行的归组治理结果。 */
export function mapCustomizationReassignResult(
  raw: OpportunityCustomizationReassignResultRaw,
): OpportunityCustomizationReassignResult {
  return {
    updatedProjectIds: raw.updated_project_ids ?? [],
    previousOpportunityIds: raw.previous_opportunity_ids ?? {},
    legacySyncStatus: raw.legacy_sync_status,
    legacyIdentityGovernanceStatus: raw.legacy_identity_governance_status,
    canonicalGroupNo: raw.canonical_group_no ?? undefined,
    legacyIdentityGovernanceReason: raw.legacy_identity_governance_reason ?? undefined,
  };
}

/** 映射通用选项。 */
function mapSelectOption(raw: OpportunitySelectOptionRaw): OpportunitySelectOption {
  return {
    value: raw.value,
    label: raw.label,
  };
}

/** 映射员工候选。 */
function mapStaffOption(raw: OpportunityStaffOptionRaw): OpportunityStaffOption {
  return {
    id: raw.id,
    username: raw.username,
    displayName: raw.display_name,
    disabled: raw.disabled,
  };
}

/** 映射阶段日志项。 */
function mapStageLogItem(raw: OpportunityStageLogItemRaw): OpportunityStageLogItem {
  return {
    id: raw.id,
    opportunityId: raw.opportunity_id,
    fromStage: raw.from_stage ?? undefined,
    toStage: raw.to_stage,
    triggerSource: raw.trigger_source,
    sourceObjectType: raw.source_object_type ?? undefined,
    sourceObjectId: raw.source_object_id ?? undefined,
    changedByUserId: raw.changed_by_user_id ?? undefined,
    changedByUserName: raw.changed_by_user_name ?? undefined,
    changedAt: raw.changed_at,
    reason: raw.reason ?? undefined,
    snapshotJson: raw.snapshot_json ?? undefined,
  };
}

/** 将阶段线路图接口响应转换为前端领域模型。 */
export function mapStageBoard(raw: OpportunityStageBoardRaw): OpportunityStageBoard {
  return {
    opportunityId: raw.opportunity_id,
    opportunityCode: raw.opportunity_code,
    opportunityName: raw.opportunity_name,
    currentStage: raw.current_stage ?? undefined,
    currentStageLabel: raw.current_stage_label ?? undefined,
    stageSource: raw.stage_source === "manual" || raw.stage_source === "derived" ? raw.stage_source : "unknown",
    stageStalledSince: raw.stage_stalled_since ?? undefined,
    stageStalledDays: raw.stage_stalled_days ?? 0,
    stageStalled: raw.stage_stalled ?? false,
    stageHistoryMissing: raw.stage_history_missing ?? false,
    manualStageCode: raw.manual_stage_code ?? undefined,
    manualStageNote: raw.manual_stage_note ?? undefined,
    manualStageSetByUserId: raw.manual_stage_set_by_user_id ?? undefined,
    manualStageSetAt: raw.manual_stage_set_at ?? undefined,
    nodes: (raw.nodes ?? []).map((node): OpportunityStageNode => ({
      code: node.code,
      label: node.label,
      sequence: node.sequence,
      kind: node.kind,
      status: node.status,
      lastChangedAt: node.last_changed_at ?? undefined,
      changedByUserName: node.changed_by_user_name ?? undefined,
      triggerSource: node.trigger_source ?? undefined,
      summary: node.summary ?? undefined,
      evidenceCount: node.evidence_count ?? 0,
      manualNote: node.manual_note ?? undefined,
      history: (node.history ?? []).map(mapStageLogItem),
    })),
  };
}

/** 映射阶段负责人项。 */
function mapStageOwnerItem(raw: OpportunityStageOwnerItemRaw): OpportunityStageOwnerItem {
  return {
    stageGroup: raw.stage_group,
    ownerUserId: raw.owner_user_id ?? undefined,
    ownerUserName: raw.owner_user_name ?? undefined,
    status: raw.status ?? "active",
    assignedAt: raw.assigned_at ?? undefined,
  };
}

/** 映射商机关联定制项。 */
function mapCustomizationProjectItem(
  raw: OpportunityCustomizationProjectItemRaw,
): OpportunityCustomizationProjectItem {
  return {
    projectId: raw.project_id,
    projectCode: raw.project_code ?? undefined,
    projectName: raw.project_name,
    selectionStatus: raw.selection_status,
    currentDemandId: raw.current_demand_id ?? undefined,
    currentDemandVersionNumber: raw.current_demand_version_number ?? undefined,
    effectiveDemandId: raw.effective_demand_id ?? undefined,
    effectiveItineraryId: raw.effective_itinerary_id ?? undefined,
    effectiveSource: raw.effective_source ?? undefined,
    sourceTraceSummary: raw.source_trace_summary ?? undefined,
    demandSummary: raw.demand_summary ?? undefined,
    traceCount: raw.trace_count ?? 0,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  };
}

/** 归一化可选文本参数。 */
function normalizeOptionalText(value?: string) {
  const normalized = value?.trim();
  return normalized ? normalized : undefined;
}
